import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import moment from 'moment'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { Box, Grid, TextField } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import {
  BaseDialog,
  FileUploadInput,
  Label,
  LoadingFeedback,
  Select,
} from 'components'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

import useSnackbar from 'hooks/useSnackbar'

import schema from './schema'

const KpiMetricsDialog = ({
  open,
  setOpen,
  refresh,
  monitoringManagementKpi,
  monitoringManagementKpiMetric,
  addMetric,
  isEdit,
  isShow,
  isCreate,
}) => {
  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()
  const formMethods = useForm({
    validationSchema: schema({ isCreate, isEdit, addMetric }),
  })

  const { control, errors, handleSubmit, formState } = formMethods
  const { dirtyFields } = formState

  const { STATUS_OPTIONS } = constants.monitoringManagementKpi

  useEffect(() => {
    if (!monitoringManagementKpiMetric) return

    formMethods.reset({
      value: monitoringManagementKpiMetric?.value ?? '',
      period: monitoringManagementKpiMetric?.period
        ? moment(monitoringManagementKpiMetric.period)
        : null,
      status: monitoringManagementKpiMetric?.status,
      date: monitoringManagementKpiMetric?.date
        ? moment(monitoringManagementKpiMetric.date)
        : null,
      observation: monitoringManagementKpiMetric?.observation ?? '',
      attachments: monitoringManagementKpiMetric?.attachments ?? null,
    })
  }, [monitoringManagementKpiMetric])

  const handleClose = () => setOpen(false)

  const handleRemoveApiFile = async (attachmentId) => {
    return await service.dponet.monitoringManagementKpiMetrics.destroyAttachment(
      {
        monitoringManagementKpiId: monitoringManagementKpi?.id,
        monitoringManagementKpiMetricsId: monitoringManagementKpiMetric?.id,
        attachmentId,
      },
    )
  }

  const handleMetrics = async (data) => {
    try {
      setLoading(true)
      if (isCreate) {
        const date = helpers.formatters.toISOStringVerify(data?.date)
        const period = helpers.formatters.toISOStringVerify(data?.period)
        await service.dponet.monitoringManagementKpiMetrics.create({
          ...data,
          monitoringManagementKpiId: monitoringManagementKpi?.id,
          assigneeId: monitoringManagementKpi?.assignee?.id,
          date,
          period,
        })

        snackbar.open({
          message: 'Métrica criada com sucesso.',
          variant: 'success',
        })
      }
      if ((isEdit || addMetric) && dirtyFields.size > 0) {
        const changedData = Array.from(dirtyFields).reduce((acc, key) => {
          if (key !== 'attachments') acc[key] = data[key]
          return acc
        }, {})

        if (changedData.date !== undefined)
          changedData.date = helpers.formatters.toISOStringVerify(
            changedData.date,
          )
        if (changedData.period !== undefined)
          changedData.period = helpers.formatters.toISOStringVerify(
            changedData.period,
          )

        if (Object.keys(changedData).length > 0) {
          await service.dponet.monitoringManagementKpiMetrics.update({
            ...changedData,
            monitoringManagementKpiId: monitoringManagementKpi?.id,
            monitoringManagementKpiMetricsId: monitoringManagementKpiMetric?.id,
          })
        }

        const newFiles = data.attachments
          ? Array.from(data.attachments).filter((f) => f instanceof File)
          : []

        if (newFiles.length > 0) {
          await service.dponet.monitoringManagementKpiMetrics.addAttachment({
            monitoringManagementKpiId: monitoringManagementKpi?.id,
            monitoringManagementKpiMetricsId: monitoringManagementKpiMetric?.id,
            files: newFiles,
          })
        }

        snackbar.open({
          message: 'Métrica editada com sucesso.',
          variant: 'success',
        })
      }

      handleClose()
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error.response.data.error) ||
          (isEdit ? 'Erro ao editar KPI.' : 'Erro ao criar KPI.'),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading} />
      <BaseDialog
        open={open}
        setOpen={setOpen}
        title={
          isCreate
            ? 'Criar Métrica'
            : addMetric
            ? 'Adicionar Métrica'
            : isEdit
            ? 'Editar Métrica'
            : 'Visualizar Métrica'
        }
        closeButtonText={!isShow ? 'Voltar' : undefined}
        actionButton={isShow ? handleClose : handleSubmit(handleMetrics)}
        actionButtonText={
          isShow ? 'Fechar' : addMetric ? 'Adicionar' : 'Salvar'
        }
        dialogSize="md"
        justifyActions="flex-end"
        fullWidth
        noPadding
      >
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(1)}
        >
          <FormContext {...formMethods}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={addMetric ? 12 : 2}>
                <Controller
                  control={control}
                  name="value"
                  defaultValue={monitoringManagementKpiMetric?.value ?? ''}
                  as={
                    <TextField
                      label="Valor Apurado"
                      fullWidth
                      variant="outlined"
                      disabled={isShow}
                      error={!!errors?.value}
                      helperText={errors?.value?.message}
                      FormHelperTextProps={{
                        style: {
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        },
                      }}
                    />
                  }
                />
              </Grid>
              {!addMetric && (
                <>
                  <Grid item xs={12} md={3}>
                    <Controller
                      as={
                        <KeyboardDatePicker
                          fullWidth
                          format="MM/yyyy"
                          views={['year', 'month']}
                          openTo="month"
                          label="Período de Referência"
                          helperText={errors?.period?.message}
                          error={!!errors?.period}
                          inputVariant="outlined"
                          InputLabelProps={{ shrink: true }}
                          onChange={(newValue) => ({ value: newValue })}
                          disabled={isShow}
                          FormHelperTextProps={{
                            style: {
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            },
                          }}
                        />
                      }
                      control={control}
                      name="period"
                      defaultValue={null}
                      mode="onChange"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      as={
                        <Select
                          label="Status da Meta"
                          fieldValue="value"
                          fieldName="name"
                          items={STATUS_OPTIONS}
                          error={!!errors?.status}
                          helperText={errors?.status?.message}
                          InputLabelProps={{ shrink: true }}
                          FormHelperTextProps={{
                            style: {
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            },
                          }}
                        />
                      }
                      name="status"
                      control={control}
                      defaultValue={null}
                      disabled={isShow}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      as={
                        <KeyboardDatePicker
                          fullWidth
                          format="DD/MM/yyyy"
                          label="Data de Periodicidade"
                          helperText={errors?.date?.message}
                          error={!!errors?.date}
                          inputVariant="outlined"
                          onChange={(newValue) => ({ value: newValue })}
                          disabled={isShow}
                          FormHelperTextProps={{
                            style: {
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                            },
                          }}
                        />
                      }
                      control={control}
                      name="date"
                      defaultValue={null}
                      mode="onChange"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Registrado por"
                      fullWidth
                      variant="outlined"
                      disabled={true}
                      defaultValue={
                        monitoringManagementKpiMetric?.assignee?.name ||
                        monitoringManagementKpi?.assignee?.name
                      }
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="observation"
                  defaultValue=""
                  disabled={isShow}
                  as={
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={5}
                      label="Observação"
                      error={!!errors?.observation}
                      helperText={errors?.observation?.message}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Label title="Anexe os documentos relacionados a medida">
                  <FileUploadInput
                    controlName="attachments"
                    hasDownloadFile
                    controllerBar
                    multiple
                    disabled={isShow}
                    onRemoveApiFile={
                      monitoringManagementKpiMetric?.id
                        ? handleRemoveApiFile
                        : null
                    }
                  />
                </Label>
              </Grid>
            </Grid>
          </FormContext>
        </Box>
      </BaseDialog>
    </>
  )
}

KpiMetricsDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  title: PropTypes.string,
  actionButtonText: PropTypes.string,
  description: PropTypes.string,
}

export default KpiMetricsDialog
